/**
 * Post-login / app-boot prefetcher.
 *
 * Loads the user's profile + saved payment accounts + payment terms once,
 * evaluates the three "redemption readiness" milestones, and writes the
 * resulting flags into the persistent `userRedemptionStatusCache` so the
 * rest of the dashboard can skip these network calls on subsequent loads.
 *
 * Each milestone that already reads `true` from the cache is skipped — so
 * a fully-onboarded user pays zero network cost on subsequent logins.
 *
 * The prefetch runs in the background and never throws to its caller.
 */

import { jwtDecode } from 'jwt-decode';
import api from './axiosInstance';
import { API } from '../config/apiConfig';
import {
  readUserRedemptionStatusCache,
  mergeUserRedemptionStatusCache,
  evaluateProfileCompleteFromUser,
  evaluateHasPaymentAccountFromAccounts,
  evaluateTermsAcceptedFromPayloads,
} from './userRedemptionStatusCache';
import {
  readUserSessionProfileCache,
  writeUserSessionProfileCache,
} from './userDashboardHomeCache';

function userIdFromToken() {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    const uid = Number(decoded?.userId ?? decoded?.UserID ?? decoded?.id);
    return Number.isFinite(uid) ? uid : null;
  } catch {
    return null;
  }
}

let inFlight = null;

/**
 * Prefetch redemption-readiness data and write to the persistent cache.
 * Returns the latest cached status. Multiple concurrent calls de-dupe to
 * a single network round-trip.
 */
export async function prefetchUserRedemptionStatus() {
  if (inFlight) return inFlight;
  const userId = userIdFromToken();
  if (!userId) return null;
  const cached = readUserRedemptionStatusCache(userId);
  if (
    cached &&
    cached.profileComplete &&
    cached.hasPaymentAccount &&
    cached.paymentTermsAccepted
  ) {
    return cached;
  }

  inFlight = (async () => {
    let profileUser = null;
    let accounts = null;
    let paymentSettings = null;

    // --- profile ---------------------------------------------------------
    if (!cached?.profileComplete) {
      try {
        // Prefer the existing session cache to avoid duplicate work
        const sessionPayload = readUserSessionProfileCache(userId)?.profile;
        if (sessionPayload) {
          profileUser = sessionPayload.user || sessionPayload;
        } else {
          const res = await api.get(API.userProfile(userId));
          const payload = res?.data || {};
          writeUserSessionProfileCache(userId, payload);
          profileUser = payload?.user || payload || null;
        }
      } catch {
        profileUser = null;
      }
    }

    // --- saved payment accounts -----------------------------------------
    if (!cached?.hasPaymentAccount) {
      try {
        const res = await api.get(API.savedPaymentAccounts);
        accounts = Array.isArray(res?.data?.accounts) ? res.data.accounts : [];
      } catch {
        accounts = null;
      }
    }

    // --- payment settings (terms acceptance) ----------------------------
    if (!cached?.paymentTermsAccepted) {
      // The profile itself may already carry PaymentTermsAccepted — only call
      // the settings endpoint if the profile didn't confirm it for us.
      const profileSaysAccepted = evaluateTermsAcceptedFromPayloads(profileUser, null);
      if (!profileSaysAccepted) {
        try {
          const res = await api.get(API.paymentSettings);
          paymentSettings = res?.data?.data || res?.data || {};
        } catch {
          paymentSettings = null;
        }
      } else {
        paymentSettings = { PaymentTermsAccepted: true };
      }
    }

    const partial = {};
    if (profileUser && evaluateProfileCompleteFromUser(profileUser)) {
      partial.profileComplete = true;
    }
    if (Array.isArray(accounts)) {
      const cc =
        profileUser?.CountryCode ||
        profileUser?.countryCode ||
        profileUser?.Country ||
        profileUser?.country ||
        '';
      if (evaluateHasPaymentAccountFromAccounts(accounts, cc)) {
        partial.hasPaymentAccount = true;
      }
    }
    if (evaluateTermsAcceptedFromPayloads(profileUser, paymentSettings)) {
      partial.paymentTermsAccepted = true;
    }
    // Always touch the cache once evaluation has run — even when no flag
    // flipped to true — so consumers can distinguish "we evaluated and the
    // user is still incomplete" (cache exists, flags still false) from
    // "we have never evaluated this user yet" (cache missing).
    mergeUserRedemptionStatusCache(userId, partial);
    return readUserRedemptionStatusCache(userId);
  })()
    .catch(() => readUserRedemptionStatusCache(userId))
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}
